import { PriorityCards } from "@components";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CopilotHeader, InsightsList } from "../components";
import {
	COPILOT_DEFAULT_PAGE_SIZE,
	COPILOT_LIST_STATUS,
	COPILOT_TAB_QUERY_PARAM,
} from "../constants";
import { useInsightsQuery } from "../query";
import type {
	CopilotListStatusFilter,
	CopilotTabKey,
	InsightCategory,
	InsightQueryParams,
} from "../types";

const tabQueryValueToCopilotTab = (value: string | null): CopilotTabKey => {
	if (!value) {
		return "all";
	}

	if (
		value === "all" ||
		value === "reminders" ||
		value === "alerts" ||
		value === "opportunities"
	) {
		return value;
	}

	const legacy: Record<string, CopilotTabKey> = {
		todas: "all",
		lembrete: "reminders",
		alerta: "alerts",
		oportunidade: "opportunities",
	};

	const mapped = legacy[value];
	if (mapped) {
		return mapped;
	}

	return "all";
};

const tabToCategory = (tab: CopilotTabKey): InsightCategory | undefined => {
	if (tab === "all") {
		return;
	}

	if (tab === "reminders") {
		return 1;
	}

	if (tab === "alerts") {
		return 2;
	}

	return 3;
};

const CopilotPage = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [currentPage, setCurrentPage] = useState(1);
	const [search, setSearch] = useState("");
	const [listStatusFilter, setListStatusFilter] =
		useState<CopilotListStatusFilter>(COPILOT_LIST_STATUS.ALL);
	const initialTab = searchParams.get(COPILOT_TAB_QUERY_PARAM);
	const [activeTab, setActiveTab] = useState<CopilotTabKey>(() =>
		tabQueryValueToCopilotTab(initialTab)
	);

	const insightQueryParams: InsightQueryParams = useMemo(() => {
		const category = tabToCategory(activeTab);
		const base: InsightQueryParams = {
			pageNumber: currentPage,
			pageSize: COPILOT_DEFAULT_PAGE_SIZE,
			resolutionStatus: listStatusFilter,
			search: search.length > 0 ? search : null,
			sortBy: null,
			sortDirection: null,
		};

		if (category !== undefined) {
			base.category = category;
		}

		return base;
	}, [activeTab, currentPage, listStatusFilter, search]);

	const insightsQuery = useInsightsQuery(insightQueryParams);

	const handleTabChange = (tab: CopilotTabKey) => {
		setActiveTab(tab);
		setCurrentPage(1);
		setSearchParams({ [COPILOT_TAB_QUERY_PARAM]: tab });
	};

	const handleListStatusFilterChange = (value: CopilotListStatusFilter) => {
		setListStatusFilter(value);
		setCurrentPage(1);
	};

	const handleSearchChange = (value: string) => {
		setSearch(value);
		setCurrentPage(1);
	};

	const tabParam = searchParams.get(COPILOT_TAB_QUERY_PARAM);

	useEffect(() => {
		const nextTab = tabQueryValueToCopilotTab(tabParam);
		setActiveTab(nextTab);
		setCurrentPage(1);
	}, [tabParam]);

	const listData = insightsQuery.data;
	const items = listData?.items ?? [];
	const totalPages = Math.max(1, listData?.totalPages ?? 1);
	const effectiveCurrentPage = listData?.currentPage ?? currentPage;

	return (
		<div className="w-full space-y-6 px-6 py-8">
			<CopilotHeader />

			<PriorityCards
				subtitle="Um resumo de prioridades com base nas informações da sua frota conosco."
				title="Resumo de insights do copiloto"
			/>

			<InsightsList
				activeTab={activeTab}
				currentPage={effectiveCurrentPage}
				errorMessage={
					insightsQuery.isError
						? "Não foi possível carregar os insights."
						: null
				}
				insights={items}
				isLoading={insightsQuery.isPending}
				listStatusFilter={listStatusFilter}
				onListStatusFilterChange={handleListStatusFilterChange}
				onPageChange={setCurrentPage}
				onRetry={() => {
					insightsQuery.refetch();
				}}
				onSearchChange={handleSearchChange}
				onTabChange={handleTabChange}
				totalPages={totalPages}
			/>
		</div>
	);
};

export default CopilotPage;
